import React, { useCallback } from "react";
import IconIphone from "@material-ui/icons/PhoneIphone";
import IconAndroid from "@material-ui/icons/PhoneAndroid";
import videoDownloadApp from "lib/assets/app-download-modal.mp4";
import IconCloseModal from "lib/assets/close-modal.svg";
import Modal, { ModalContent } from "./Modal";
import { useTranslations } from "lib/i18n";
import { useTheme } from "lib/contexts/themeContext";
import { getAirbridgeStoreLink } from "lib/airbridge";

/**
 * The store links report themselves, not the page they were opened from: one
 * campaign for every mount site, split from the QR's installs by ad_group.
 */
const CAMPAIGN = "app_download_modal";
const ADGROUP = "text_link";

const QR_SIZE = 180;
/**
 * The white padding is also the code's quiet zone, which the SVG itself does
 * not carry. 180px over a 29-module grid is ~6.2px per module, so 24px clears
 * the ~4 modules scanners expect.
 */
const QR_QUIET_ZONE = 24;

interface ModalDownloadAppProps {
  open: boolean;
  onClose: () => void;
  /**
   * The page's Airbridge tracker QR code, from `lib/assets/qr-app-download-*`.
   * Required so that a new mount site cannot silently attribute its installs to
   * another page -- each code maps to its own ad_group in Airbridge.
   */
  qr: React.FC<React.SVGProps<SVGSVGElement>>;
}

const ModalDownloadApp: React.FC<ModalDownloadAppProps> = ({
  open,
  onClose,
  qr: QR,
}) => {
  const t = useTranslations("common");
  const { breakpoints } = useTheme();

  const handleOnVideoToggle = useCallback(() => {
    const video = document.getElementById("video-download-app");
    if (video instanceof HTMLVideoElement) {
      if (video.paused || video.ended) {
        video.play();
      } else {
        video.pause();
      }
    }
  }, []);

  return (
    <Modal
      css={`
        &&& .modal-paper-root {
          max-width: 720px;
          width: 100%;
          background-color: #f1f2f3;
        }
      `}
      open={open}
      onClose={onClose}
    >
      <ModalContent>
        {/* close button */}
        <div
          css={`
            margin: 0 0 10px;
            text-align: right;
          `}
        >
          <button
            css={`
              margin: 0;
              padding: 0;
              border: none;
              background: none;
              cursor: pointer;
            `}
            onClick={onClose}
          >
            <IconCloseModal />
          </button>
        </div>
        {/* the copy runs full width -- it wraps badly inside the QR column */}
        <h2
          css={`
            margin: 0 0 8px;
            line-height: 28px;
            font-size: 20px;
            font-weight: 700;
          `}
        >
          {t("downloadApp.scanTitle")}
        </h2>
        <p
          css={`
            margin: 0 0 24px;
            line-height: 22px;
            font-size: 14px;
            color: #777777;
          `}
        >
          {t("downloadApp.scanDescription")}
        </p>
        <div
          css={`
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 24px;
            ${breakpoints.down("sm")} {
              flex-direction: column;
            }
          `}
        >
          {/* app preview */}
          <div
            css={`
              flex: 1;
              min-width: 0;
              width: 100%;
            `}
          >
            <div
              css={`
                position: relative;
                border-radius: 20px;
                padding-bottom: ${(371.25 / 660) * 100}%;
                width: 100%;
                height: 0;
                background-color: #f1f3f5;
              `}
            >
              <video
                id="video-download-app"
                css={`
                  border-radius: 20px;
                  width: 100%;
                `}
                autoPlay
                muted
                onClick={handleOnVideoToggle}
                onEnded={handleOnVideoToggle}
              >
                <source src={videoDownloadApp} type="video/mp4" />
              </video>
            </div>
          </div>
          {/*
            The card carries the layout: a `css` prop on `QR` itself would be
            hoisted to module scope by babel-plugin-styled-components, where
            this destructured local is out of scope.
          */}
          <div
            css={`
              flex-shrink: 0;
              border-radius: 16px;
              padding: ${QR_QUIET_ZONE}px;
              box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
              background-color: white;
              svg {
                display: block;
              }
            `}
          >
            <QR width={QR_SIZE} height={QR_SIZE} />
          </div>
        </div>
        {/*
          The escape hatch for anyone who cannot scan -- a webcam-less desktop,
          a locked-down camera app, a screen reader. Below the whole row rather
          than under the code, so it stays out of the way of the code's
          alignment with the preview beside it.
        */}
        <div
          css={`
            padding: 8px 0;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 16px;
            a {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              line-height: 20px;
              font-size: 14px;
              color: #777777;
              :hover {
                color: ${({ theme }) => theme.palette.primary.main};
                text-decoration: underline;
              }
            }
            svg {
              font-size: 18px;
            }
          `}
        >
          <a
            href={getAirbridgeStoreLink("ios", CAMPAIGN, ADGROUP)}
            target="_blank"
            rel="noreferrer"
          >
            <IconIphone />
            iPhone
          </a>
          <a
            href={getAirbridgeStoreLink("android", CAMPAIGN, ADGROUP)}
            target="_blank"
            rel="noreferrer"
          >
            <IconAndroid />
            Android
          </a>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ModalDownloadApp;
